import useNavigate from "core/hooks/useNavigate";
import { useId, useState } from "react";
import { useLocation } from "react-router";
import styled from "styled-components";

const TabsContainer = styled.div`
  width: 100%;
  //   margin-bottom: 16px;

  ${(props) =>
    props.isRouter &&
    `
    margin-bottom: 0px;
  `}
`;

const TabsList = styled.div`
  box-sizing: border-box;
  width: fit-content;
  //   border: 1px solid #541299;
  border-radius: 5px;
  display: flex;
  align-items: center;
  align-content: center;
  margin-bottom: 16px;
  overflow: hidden; // Cuts off child tab border radius

  ${(props) =>
    props.basis === "full" &&
    `
      width: 100%;
  `}

  ${(props) =>
    props.isRouter &&
    `
    margin-bottom: 0px;
  `}
`;

// A real <button> so triggers are focusable and activate on Enter/Space for
// free. The font/appearance resets keep it looking identical to the old <div>.
const TabTrigger = styled.button`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 16px;
  text-align: center;
  font-family: inherit;
  font-size: 16px;
  line-height: 18px;
  color: #541299;
  background-color: #ffffff;
  border: 1px solid #541299;

  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #ddcdef;
  }

  &:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  &:last-child {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  &:not(:first-child):not(:last-child) {
    border-left: none;
    border-right: none;
  }

  ${(props) =>
    props.isActive &&
    `
      background-color: #541299;
      color: #ffffff;
      cursor: default;

      &:hover {
        background-color: #6d36a5;
        color: #ffffff;

      }
  `}

  ${(props) =>
    props.basis === "full" &&
    `
        flex: 1;
  `}

  ${(props) =>
    props.isDisabled &&
    `
        color: #393D3F;
        background: #DADDE1;
        border-color: #DADDE1;
        pointer-events: none;
  `}
`;

/**
 * Base properties shared by all tab items.
 * @typedef {object} TabItemBase
 * @property {string} id - Unique identifier for the tab.
 * @property {string} title - Display text for the tab trigger.
 * @property {boolean} [disabled] - Whether the tab is disabled.
 */

/**
 * Tab item for type="content". Renders inline content when active.
 * @typedef {TabItemBase & { content: JSX.Element }} ContentTabItem
 */

/**
 * Tab item for type="router". Navigates to a route when clicked.
 * @typedef {TabItemBase & { to?: string }} RouterTabItem
 * @property {string} [to] - Custom navigation path. Falls back to `id` if not provided.
 */

/**
 * The properties for the Tabs.
 * @typedef {object} TabsProps
 * @property {"content" | "router"} [type] The type of tabs to render.
 * - "content" - Tab changes automatically render that tab's content
 * - "router" - Tab changes result in updates to the url
 * @property {ContentTabItem[] | RouterTabItem[]} items - Array of tab items.
 * Items should match the shape for the given `type`:
 * - type="content": `{ id, title, disabled?, content }`
 * - type="router": `{ id, title, disabled?, to? }`
 * @property {"dynamic" | "full"} [basis] How the tab widths are determined.
 * - "dynamic" - Dynamically set the width of tabs based on text
 * - "full" - Expand the tabs to the full width of the container
 */

/**
 * Component for rendering tabs.
 *
 * @param {TabsProps} props - The component props.
 * @returns {JSX.Element} A React element.
 */
const Tabs = ({ type = "content", items, basis = "dynamic" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeContentId, setActiveContentId] = useState(
    items ? items[0].id : null
  );

  // Prefix for the generated tab/tabpanel ids used by the aria wiring below.
  const idPrefix = useId();

  if (type === "router") {
    return (
      <TabsContainer isRouter>
        <TabsList basis={basis} isRouter role="tablist">
          {items.map((item) => {
            const isActive = location.pathname.endsWith(item.id);

            return (
              <TabTrigger
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                basis={basis}
                isActive={isActive}
                isDisabled={item.disabled}
                disabled={item.disabled}
                onClick={() => {
                  // Sometimes "to" is provided if navigation requires a directory change
                  navigate(item.to ?? item.id);
                }}
              >
                {item.title}
              </TabTrigger>
            );
          })}
        </TabsList>
      </TabsContainer>
    );
  }

  // Default return type === "content"
  return (
    <TabsContainer>
      <TabsList basis={basis} role="tablist">
        {items.map((item) => {
          const isActive = item.id === activeContentId;

          return (
            <TabTrigger
              key={item.id}
              id={`${idPrefix}-tab-${item.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`${idPrefix}-panel-${item.id}`}
              basis={basis}
              isActive={isActive}
              isDisabled={item.disabled}
              disabled={item.disabled}
              onClick={() => {
                setActiveContentId(item.id);
              }}
            >
              {item.title}
            </TabTrigger>
          );
        })}
      </TabsList>
      <div
        role="tabpanel"
        id={`${idPrefix}-panel-${activeContentId}`}
        aria-labelledby={`${idPrefix}-tab-${activeContentId}`}
      >
        {items.find((item) => item.id === activeContentId)?.content}
      </div>
    </TabsContainer>
  );
};

export default Tabs;
